import { useMemo } from 'react'

// Simple custody-day calculator based on schedule type
// Returns which household has the child on a given date
function getHomeForDate(schedule, date) {
  const start = new Date(schedule.start_date)
  const target = new Date(date)
  const diffDays = Math.floor((target - start) / 86400000)
  if (diffDays < 0) return null

  switch (schedule.schedule_type) {
    case 'alternating_weeks':
      return Math.floor(diffDays / 7) % 2 === 0 ? schedule.household_id : schedule.linked_household_id
    case 'two_two_three': {
      const cycle = diffDays % 7
      return cycle < 2 ? schedule.household_id : cycle < 4 ? schedule.linked_household_id : schedule.household_id
    }
    case 'every_weekend': {
      const dow = target.getDay()
      return (dow === 0 || dow === 6) ? schedule.linked_household_id : schedule.household_id
    }
    default:
      return schedule.household_id
  }
}

export function CustodyTransitionsPanel({ data }) {
  const { custody = [], members = [], household } = data

  const activeSchedules = custody.filter((s) => s.is_active)
  if (activeSchedules.length === 0) return null

  const memberMap = useMemo(() =>
    Object.fromEntries(members.map((m) => [m.id, m])),
    [members]
  )

  const today = new Date()
  const todayStr = today.toISOString().split('T')[0]

  const childStatuses = activeSchedules.map((s) => {
    const child = memberMap[s.child_id]
    const currentHome = getHomeForDate(s, todayStr)
    const isHome = currentHome === household?.id

    // Find next 3 transitions
    const transitions = []
    for (let i = 1; transitions.length < 3 && i <= 30; i++) {
      const d = new Date(today.getTime() + i * 86400000)
      const ds = d.toISOString().split('T')[0]
      const prevDs = new Date(today.getTime() + (i - 1) * 86400000).toISOString().split('T')[0]
      const cur = getHomeForDate(s, ds)
      const prev = getHomeForDate(s, prevDs)
      if (cur !== prev) {
        transitions.push({
          date: ds,
          direction: cur === household?.id ? 'arriving' : 'departing',
          label: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
        })
      }
    }
    return { schedule: s, child, isHome, transitions }
  })

  return (
    <div className="flex flex-col h-full px-4 sm:px-8 py-4 sm:py-6 panel-animate-in min-h-0">
      <p className="text-gray-500 text-xs sm:text-sm font-semibold uppercase tracking-widest mb-3">Custody Schedule</p>

      <div className="flex-1 space-y-4 overflow-y-auto min-h-0 scrollbar-hide">
        {childStatuses.map(({ schedule, child, isHome, transitions }) => (
          <div key={schedule.id} className="bg-white/8 rounded-xl sm:rounded-2xl p-4 sm:p-5">
            <div className="flex items-center gap-3 sm:gap-4 mb-3">
              <div
                className="w-10 h-10 sm:w-14 sm:h-14 rounded-full flex items-center justify-center text-white text-base sm:text-xl font-bold flex-shrink-0"
                style={{ backgroundColor: schedule.color }}
              >
                {child?.displayName?.[0]?.toUpperCase() ?? '?'}
              </div>
              <div>
                <p className="text-white text-lg sm:text-2xl font-bold">{child?.displayName ?? 'Unknown'}</p>
                <p className={`text-sm sm:text-base font-medium ${isHome ? 'text-emerald-400' : 'text-gray-400'}`}>
                  {isHome ? '🏠 Home today' : '✈️ Away today'}
                </p>
              </div>
            </div>

            {transitions.length > 0 && (
              <div className="space-y-1.5">
                {transitions.map((t) => (
                  <div key={t.date} className="flex items-center gap-3 text-xs sm:text-sm">
                    <span className="text-gray-400 w-28 sm:w-32">{t.label}</span>
                    <span className={t.direction === 'arriving' ? 'text-emerald-400' : 'text-amber-400'}>
                      {t.direction === 'arriving' ? '→ Arriving' : '← Departing'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
